import { forwardRef, useImperativeHandle } from 'react';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { DateHelpers } from '@/helpers/date-helpers';
import RenderRateShop from '@/renderers/render-rate-shop';
import { TableHeader } from './rate-shop-table/table-header';
import { TablePagination } from './rate-shop-table/table-pagination';
import { useTablePagination } from './shared/hooks/use-table-pagination';
import { useTableSorting } from './shared/hooks/use-table-sorting';
import type { RateShopData } from './shared/types/rate-shop-types';
import { generateExportFilename } from './shared/utils/rate-formatters';

interface RateShopTableProps {
  brandCode: string;
  data: RateShopData;
  rateType: 'lowest' | 'best-flex';
  meetingCategory?: string;
}

export interface RateShopTableRef {
  exportToCSV: () => void;
}

export const RateShopTable = forwardRef<RateShopTableRef, RateShopTableProps>(
  ({ brandCode, data, rateType, meetingCategory }, ref) => {
    const { headers, rows } = data;

    const { sortedData, handleSort, getSortIcon } = useTableSorting({
      data: rows,
      headers,
    });

    const { paginatedData, paginationConfig, goToPage, goToPrevious, goToNext, resetToFirstPage } =
      useTablePagination({ data: sortedData });

    useImperativeHandle(ref, () => ({
      exportToCSV,
    }));

    const exportToCSV = () => {
      const csvContent = [
        headers.map((header) => header.title).join(','),
        ...sortedData.map((row) =>
          row
            .map((cell) => {
              const displayValue = cell?.displayValue || cell?.value || '';
              return `"${displayValue}"`;
            })
            .join(','),
        ),
      ].join('\n');

      const filename = generateExportFilename({
        brandCode,
        rateType,
        date: DateHelpers.today(),
        filename: '', // Not used in this context
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <div className='space-y-4'>
        <div className='rounded-md border'>
          <Table>
            <TableHeader
              headers={headers}
              onSort={(dataKey) => {
                handleSort(dataKey);
                resetToFirstPage();
              }}
              getSortIcon={getSortIcon}
            />
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index}>
                  {headers.map((header, headerIdx) => {
                    const cellClass =
                      headerIdx === 0
                        ? 'px-3 py-1 text-left text-sm font-medium text-gray-900 whitespace-nowrap'
                        : 'px-3 py-1 text-center text-sm font-medium text-gray-900 whitespace-nowrap';
                    const cellWithMeetingCategory = {
                      ...row[headerIdx],
                      meta: {
                        ...row[headerIdx]?.meta,
                        meetingCategory,
                      },
                    };
                    return (
                      <TableCell key={`cell-row-${index}-${header.dataKey}`} className={cellClass}>
                        <div className='w-full text-center'>
                          <RenderRateShop cell={cellWithMeetingCategory} />
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          paginationConfig={paginationConfig}
          onPageChange={goToPage}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      </div>
    );
  },
);
